/**
 * The client's game-feedback hook (story `q-demo-vfx-pass`, shared with
 * `q-demo-audio-engine-and-sfx`). ONE place says "something the player should feel just
 * happened"; presentation layers (the VFX director, the SFX engine) subscribe and decide
 * how it looks or sounds. Emitters are fixed so nothing fires twice:
 *
 * - `Game.ts` emits what it learns from the server's GameEvents and its own prediction:
 *   weapon hits, ability casts, telegraph wind-up / landing, damage taken, Dodge, deaths,
 *   respawns.
 * - `state/store.ts` emits what it learns from RPC results and pushes: gather, craft,
 *   quest turn-in, level up, own loot.
 *
 * Presentation only. No subscriber may change game state, and the payloads carry ids and
 * world points, never a tuning value. Pure TypeScript: no `three`, no DOM.
 */

/** An ability's presentation family (see `vfx/cues.ts` `ABILITY_VFX`). */
export type AbilityFeel = 'strike' | 'heal' | 'shield' | 'decoy' | 'buff' | 'dash' | 'control';

export type GameFeedbackEvent =
  /** A melee/ability blow landed on `targetId`. `heavy` = an ability or heavy swing. */
  | { kind: 'weaponHit'; sourceId: number; targetId: number; heavy: boolean }
  /**
   * A blow landed on the OWN avatar (a telegraph that hit us, an attack or strike aimed at
   * us) — from the event channel, never the snapshot (AD-031). The wire carries no damage
   * magnitude, so `amount` is the blow count (1).
   */
  | { kind: 'playerDamaged'; amount: number }
  /** Someone cast an ability; `feel` is its presentation family. */
  | { kind: 'ability'; sourceId: number; abilityId: string; feel: AbilityFeel | null }
  /** The OWN avatar started a Dodge, heading (dirX, dirZ). */
  | { kind: 'dodge'; dirX: number; dirZ: number }
  /** A hostile wind-up began / landed (centre + radius of its shape). */
  | {
      kind: 'telegraphStart';
      sourceId: number;
      x: number;
      z: number;
      radius: number;
      heavy: boolean;
    }
  | {
      kind: 'telegraphLand';
      sourceId: number;
      x: number;
      z: number;
      radius: number;
      hitSelf: boolean;
    }
  /** The OWN gather channel began on a node (the tool strikes) / answered. */
  | { kind: 'gatherStrike'; nodeId: number; resource: string }
  | { kind: 'gather'; nodeId: number; resource: string; ok: boolean }
  /** The OWN craft answered. */
  | { kind: 'craft'; ok: boolean }
  /** The OWN kill dropped loot at `entityId`'s body. `rare` = anything above common. */
  | { kind: 'loot'; entityId: number; rare: boolean }
  /** A quest objective of the OWN log turned done (AD-867: per verb, every quest). */
  | { kind: 'objectiveComplete'; name: string }
  /** A quest of the OWN log became ready to turn in (AD-867). */
  | { kind: 'questReady'; name: string }
  | { kind: 'questComplete'; name: string }
  | { kind: 'levelUp'; level: number }
  | { kind: 'died'; entityId: number; self: boolean }
  | { kind: 'respawned'; entityId: number; x: number; z: number; self: boolean };

export type GameFeedbackKind = GameFeedbackEvent['kind'];
export type FeedbackListener = (event: GameFeedbackEvent) => void;

export interface FeedbackBus {
  emit(event: GameFeedbackEvent): void;
  /** Returns the unsubscribe. */
  subscribe(listener: FeedbackListener): () => void;
  /** How many listeners are attached (tests, the DEV dump). */
  readonly size: number;
}

/**
 * A bus. A listener that throws is isolated (logged once per bus) so one presentation
 * layer failing can never stop another, or the game loop that emitted.
 */
export function createFeedbackBus(): FeedbackBus {
  const listeners = new Set<FeedbackListener>();
  let warned = false;
  return {
    emit(event) {
      for (const l of listeners) {
        try {
          l(event);
        } catch (error) {
          if (!warned) {
            warned = true;
            console.warn('[feedback] a listener failed; others continue', error);
          }
        }
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    get size() {
      return listeners.size;
    },
  };
}

/** The client's one bus. Emitters and subscribers import this. */
export const gameFeedback: FeedbackBus = createFeedbackBus();
